/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  TextField,
  MenuItem,
  Stack,
  Pagination,
  Button,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import Breadcrumb from "../../DefaultComponent/BreadCrumb";
import Spinner from "../../DefaultComponent/Spinner";
import { formatDate, calculateTotalPages } from "../../../utils/Helper";
import {
  PromoCodeCreateAction,
  PromoCodeListAction,
  PromoUpdateAction,
} from "../../../Redux/Actions/PaymentAction";
import CreatePromo, {
  PromoCategory,
  PromoCodeCreate,
  PromoType,
} from "./CreatePromo/CreatePromo";
import { useNavigate } from "react-router-dom";
import UpdatePromo, { UpdatePromoPayload } from "./UpdatePromo/UpdatePromo";

export interface PromoCode {
  id: number;
  code: string;
  title: string | null;
  description: string | null;
  category?: PromoCategory;
  type: PromoType;
  user_promo_credits: number | null;
  org_promo_credits: number | null;
  max_usage_per_account: number;
  max_usage: number | null;
  usage_count: number;
  status: string;
  tnc: string | null;
  start_at: string;
  expire_at: string | null;
  created_at: string;
  updated_at: string;
  valid_for_users: string[] | null;
  valid_for_orgs: string[] | null;
  valid_duration?: number | null;
  mark_del: boolean;
  created_by: number;
}

const initialFilters = {
  code: "",
  type: "",
  status: "",
  start_at: "",
  expire_at: "",
  sort_by: "start_at",
  sort_order: "asc",
};

const Promo: React.FC = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const PromoCodesListData = useSelector(
    (state: any) => state.PromoCodeListData
  );

  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage] = useState<number>(10);
  const [filters, setFilters] = useState(initialFilters);
  const [openForm, setOpenForm] = useState<boolean>(false);
  const [openUpdate, setOpenUpdate] = useState(false);
  const [selectedPromoId, setSelectedPromoId] = useState<number | null>(null);
  const [selectedUpdateData, setSelectedUpdateData] = useState<any>({});

  const buildQueryString = (
    pageNum: number,
    filterObj: typeof initialFilters
  ) => {
    const paramsObj: Record<string, any> = {
      page: pageNum,
      page_size: rowsPerPage,
      ...filterObj,
    };
    // remove empty
    Object.keys(paramsObj).forEach(
      (key) =>
        (paramsObj[key] == null || paramsObj[key] === "") &&
        delete paramsObj[key]
    );
    return `?${new URLSearchParams(paramsObj).toString()}`;
  };

  const fetchPromoCodes = (pageNum = page, filterObj = filters) => {
    setLoading(true);
    const qs = buildQueryString(pageNum, filterObj);
    dispatch(PromoCodeListAction(qs)).finally(() => setLoading(false));
  };

  const applyFilters = () => {
    setPage(1);
    fetchPromoCodes(1, filters);
  };

  const clearFilters = () => {
    setFilters(initialFilters);
    setPage(1);
    fetchPromoCodes(1, initialFilters);
  };

  useEffect(() => {
    fetchPromoCodes();
  }, [dispatch, page]);

  useEffect(() => {
    if (PromoCodesListData?.data?.row) {
      setPromoCodes(PromoCodesListData.data.row);
    }
  }, [PromoCodesListData]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreatePromo = (data: PromoCodeCreate) => {
    dispatch(PromoCodeCreateAction(data)).then(() => {
      fetchPromoCodes(1, initialFilters);
    });
  };

  const handlePromoUpdate = (payload: UpdatePromoPayload, params: string) => {
    dispatch(PromoUpdateAction(payload, params)).then(() => {
      fetchPromoCodes(1, initialFilters);
      setOpenUpdate(false);
    });
  };

  return (
    <>
      {loading && <Spinner />}
      <div
        className="page-wrapper card"
        style={{ display: "flex", flexDirection: "column" }}
      >
        <div className="col-md-12">
          <Breadcrumb
            pageTitle="Promo Codes"
            breadcrumbs={[
              { label: "Promo Codes", path: "/promo-codes", isActive: true },
            ]}
          />
        </div>

        <Box sx={{ m: 2, display: "flex", flexWrap: "wrap", gap: 2 }}>
          <TextField
            name="code"
            label="Code"
            value={filters.code}
            onChange={handleFilterChange}
            size="small"
          />
          <TextField
            select
            name="type"
            label="Type"
            value={filters.type}
            onChange={handleFilterChange}
            size="small"
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="USER">User</MenuItem>
            <MenuItem value="ORGANIZATION">Organization</MenuItem>
          </TextField>
          <TextField
            select
            name="status"
            label="Status"
            value={filters.status}
            onChange={handleFilterChange}
            size="small"
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="INACTIVE">Inactive</MenuItem>
          </TextField>
          <TextField
            name="start_at"
            label="Start At"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={filters.start_at}
            onChange={handleFilterChange}
            size="small"
          />
          <TextField
            name="expire_at"
            label="Expire At"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={filters.expire_at}
            onChange={handleFilterChange}
            size="small"
          />
          <Button variant="contained" onClick={applyFilters}>
            Apply Filters
          </Button>
          <Button variant="outlined" onClick={clearFilters}>
            Clear Filters
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenForm(true)}
          >
            Create Promo
          </Button>
        </Box>

        <Box sx={{ m: 2 }}>
          <TableContainer component={Paper}>
            <Table aria-label="Promo Codes Table">
              <TableHead sx={{ backgroundColor: "#0555c8" }}>
                <TableRow>
                  <TableCell align="center" sx={{ color: "white" }}>
                    S.No
                  </TableCell>
                  <TableCell sx={{ color: "white" }}>Promo Code</TableCell>
                  <TableCell sx={{ color: "white" }}>Title</TableCell>
                  <TableCell sx={{ color: "white" }}>Type</TableCell>
                  <TableCell sx={{ color: "white" }}>User Credits</TableCell>
                  <TableCell sx={{ color: "white" }}>Org Credits</TableCell>
                  <TableCell sx={{ color: "white" }}>
                    Max Usage/Account
                  </TableCell>
                  <TableCell sx={{ color: "white" }}>Max Usage</TableCell>
                  <TableCell sx={{ color: "white" }}>Usage Count</TableCell>
                  <TableCell sx={{ color: "white" }}>Status</TableCell>
                  <TableCell sx={{ color: "white" }}>Start At</TableCell>
                  <TableCell sx={{ color: "white" }}>Expire At</TableCell>
                  <TableCell sx={{ color: "white" }}>Created At</TableCell>
                  <TableCell sx={{ color: "white" }} align="center"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {promoCodes.length > 0 ? (
                  promoCodes.map((promo, index) => (
                    <TableRow
                      key={promo.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => navigate(`/promo/usage/${promo.id}`)}
                    >
                      <TableCell align="center">
                        {(page - 1) * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell>{promo.code}</TableCell>
                      <TableCell>{promo.title ?? "—"}</TableCell>
                      <TableCell>{promo.type}</TableCell>
                      <TableCell>{promo.user_promo_credits ?? "N/A"}</TableCell>
                      <TableCell>{promo.org_promo_credits ?? "N/A"}</TableCell>
                      <TableCell>
                        {promo.max_usage_per_account ?? "N/A"}
                      </TableCell>
                      <TableCell>{promo.max_usage ?? "N/A"}</TableCell>
                      <TableCell>{promo.usage_count}</TableCell>
                      <TableCell>{promo.status}</TableCell>
                      <TableCell>{formatDate(promo.start_at)}</TableCell>
                      <TableCell>
                        {promo.expire_at ? formatDate(promo.expire_at) : "N/A"}
                      </TableCell>
                      <TableCell>{formatDate(promo.created_at)}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedUpdateData(promo);
                            setSelectedPromoId(promo?.id);
                            setOpenUpdate(true);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      No Promo Codes Found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <div className="pagination-style">
          <Stack spacing={2}>
            <Pagination
              count={calculateTotalPages(
                PromoCodesListData?.data?.total_count || 0,
                rowsPerPage
              )}
              color="primary"
              variant="outlined"
              shape="rounded"
              page={page}
              onChange={(e: any, newPage) => setPage(newPage)}
            />
          </Stack>
        </div>
      </div>
      <CreatePromo
        open={openForm}
        onClose={() => setOpenForm(false)}
        onSubmit={handleCreatePromo}
      />
      {selectedPromoId !== null && (
        <UpdatePromo
          open={openUpdate}
          onClose={() => setOpenUpdate(false)}
          onSubmit={handlePromoUpdate}
          promoId={selectedPromoId}
          initialData={selectedUpdateData}
        />
      )}
    </>
  );
};

export default Promo;
